<template>
    <div class="flex items-stretch gap-1.5">
        <button
            type="button"
            class="shrink-0 flex items-center justify-center w-11 my-1.5 rounded-lg border border-gray-400 bg-white text-gray-700 transition hover:border-primary-300 hover:bg-primary-50 disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
            :class="{ hidden: !overflows }"
            :disabled="atStart"
            :aria-label="prevLabel"
            @click="nudge(-1)"
        >
            <i class="fa-solid fa-chevron-left" aria-hidden="true" />
        </button>

        <div
            ref="track"
            class="relative grow flex gap-2 overflow-x-auto scrollbar-none scroll-smooth motion-reduce:scroll-auto snap-x py-1.5"
            role="group"
            :aria-label="label"
            @scroll.passive="measure"
        >
            <slot />
        </div>

        <button
            type="button"
            class="shrink-0 flex items-center justify-center w-11 my-1.5 rounded-lg border border-gray-400 bg-white text-gray-700 transition hover:border-primary-300 hover:bg-primary-50 disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-400 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-700"
            :class="{ hidden: !overflows }"
            :disabled="atEnd"
            :aria-label="nextLabel"
            @click="nudge(1)"
        >
            <i class="fa-solid fa-chevron-right" aria-hidden="true" />
        </button>
    </div>
</template>

<script setup>
    import { onBeforeUnmount, onMounted, onUpdated, ref } from "vue";

    defineProps({
        /** Names the strip for screen readers; the arrows get their own labels. */
        label: String,
        prevLabel: String,
        nextLabel: String,
    });

    const track = ref(null);
    const overflows = ref(false);
    const atStart = ref(true);
    const atEnd = ref(true);

    let observer = null;

    // A pixel of slack: scrollLeft is fractional once the browser scales the page.
    const measure = () => {
        const element = track.value;

        if (!element) {
            return;
        }

        const furthest = element.scrollWidth - element.clientWidth;

        overflows.value = furthest > 1;
        atStart.value = element.scrollLeft <= 1;
        atEnd.value = element.scrollLeft >= furthest - 1;
    };

    // Most of a screenful, so a tap always moves you on but never past what you were reading.
    const nudge = (direction) => {
        const element = track.value;

        if (element) {
            element.scrollBy({ left: direction * Math.max(element.clientWidth * 0.75, 120) });
        }
    };

    /** Pull a chip the parent has selected programmatically into view. */
    const reveal = (element) => {
        if (track.value && element) {
            const centred = element.offsetLeft - (track.value.clientWidth - element.offsetWidth) / 2;

            track.value.scrollTo({ left: Math.max(0, centred) });
        }
    };

    defineExpose({ reveal });

    onMounted(() => {
        observer = new ResizeObserver(measure);
        observer.observe(track.value);
    });

    // Chip labels change with the day, which can change how far the strip runs.
    onUpdated(measure);

    onBeforeUnmount(() => observer?.disconnect());
</script>

<style scoped>
    /* The app paints a black-and-orange scrollbar globally; on a chip row it is noise. */
    .scrollbar-none {
        scrollbar-width: none;
    }

    .scrollbar-none::-webkit-scrollbar {
        display: none;
    }
</style>
